/**
 * The closed API error-code union (bkfc-cms `src/http/errors.ts`; the `Error` schema in
 * openapi.json). Adding a member is a /v2 change on the server, so this list is pinned.
 */
export const API_ERROR_CODES = [
  'LOGIN_REQUIRED',
  'SESSION_INVALID',
  'NOT_ENTITLED',
  'GEO_BLOCKED',
  'DEVICE_NOT_ALLOWED',
  'CONCURRENCY_LIMIT',
  'NOT_YET_AVAILABLE',
  'NOT_PUBLISHED',
  'UPSTREAM_MISCONFIGURED',
  'ENTITLEMENT_UNDETERMINED',
  'BAD_REQUEST',
  'NOT_FOUND',
  'RATE_LIMITED',
  'INTERNAL',
];

/** @typedef {typeof API_ERROR_CODES[number]} ApiErrorCode */

const isServerErrorBody = body =>
  typeof body === 'object' &&
  body !== null &&
  typeof body.error === 'object' &&
  body.error !== null &&
  'code' in body.error &&
  'message' in body.error;

export class ApiError extends Error {
  /**
   * @param {ApiErrorCode} code
   * @param {number} status
   * @param {string} message
   * @param {{retryAfterMs?: number, requestId?: string, reason?: string}} [opts]
   */
  constructor(code, status, message, opts = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.retryAfterMs = opts.retryAfterMs ?? null;
    this.requestId = opts.requestId ?? null;
    /** Optional discriminator under `code`; today only `account_deleted` on SESSION_INVALID. */
    this.reason = opts.reason ?? null;
  }

  /**
   * Maps the server error body `{ error: { code, message, retry_after_s, request_id, reason? } }`.
   * Unknown codes map to `INTERNAL`: the client never trusts a code outside the closed union.
   * @param {unknown} body
   * @param {number} status
   */
  static fromResponseBody(body, status) {
    if (!isServerErrorBody(body)) {
      return new ApiError('INTERNAL', status, 'Something went wrong.');
    }
    const { error } = body;
    const code = API_ERROR_CODES.includes(error.code) ? error.code : 'INTERNAL';
    return new ApiError(code, status, error.message, {
      retryAfterMs:
        typeof error.retry_after_s === 'number' ? error.retry_after_s * 1000 : undefined,
      requestId: error.request_id,
      reason: typeof error.reason === 'string' ? error.reason : undefined,
    });
  }
}
